import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft,
  Download,
  Filter,
  Calendar,
  TrendingUp,
  BarChart3,
  PieChart,
  DollarSign,
  Receipt,
  Users,
  CreditCard,
  CheckCircle
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import expenseAnalysisService from '../../services/expenseAnalysisService';

const ExpenseAnalysis = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ departments: [], categories: [] });
  
  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    categoryId: '',
    department: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadFilterOptions();
    loadAnalysisData();
  }, []);

  useEffect(() => {
    loadAnalysisData();
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const response = await expenseAnalysisService.getFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadAnalysisData = async () => {
    try {
      setLoading(true);
      const response = await expenseAnalysisService.getExpenseAnalysis(filters);
      if (response.success) {
        setAnalysisData(response.data);
      } else {
        toast.error(response.message || 'Failed to load expense analysis');
      }
    } catch (error) {
      console.error('Error loading expense analysis:', error);
      if (!error._errorHandled) {
        toast.error('Failed to load expense analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      categoryId: '',
      department: '',
      status: ''
    });
  };

  const exportReport = () => {
    toast.info('Export functionality coming soon');
  };

  if (loading && !analysisData) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading expense analysis...</p>
        </div>
      </div>
    );
  }

  const summary = analysisData?.summary || {};
  const monthlyTrends = analysisData?.monthlyTrends || [];
  const categoryBreakdown = analysisData?.categoryBreakdown || [];
  const statusDistribution = analysisData?.statusDistribution || [];
  const departmentBreakdown = analysisData?.departmentBreakdown || [];
  const topExpenses = analysisData?.topExpenses || [];

  // Calculate max for chart scaling
  const maxMonthlyAmount = Math.max(...monthlyTrends.map(t => t.totalAmount || 0), 1);
  const maxCategoryAmount = Math.max(...categoryBreakdown.map(c => c.totalAmount || 0), 1);

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Expenses', path: '/expenses' },
          { label: 'Expense Analysis', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Expense Analysis</h1>
          <p>Expense trends and category analysis</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            Filters
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportReport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Category
              </label>
              <select
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Categories</option>
                {filterOptions.categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.categoryName}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Department
              </label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Departments</option>
                {filterOptions.departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Under Review">Under Review</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.totalExpenses)}
          </div>
          <div className="metric-title">Total Expenses</div>
          <div className="metric-subtitle">All expense claims</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Receipt size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {summary.totalClaims || 0}
          </div>
          <div className="metric-title">Total Claims</div>
          <div className="metric-subtitle">All claims</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Average</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.avgClaimAmount)}
          </div>
          <div className="metric-title">Avg Claim Amount</div>
          <div className="metric-subtitle">Per claim average</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.approvedAmount)}
          </div>
          <div className="metric-title">Approved Amount</div>
          <div className="metric-subtitle">Approved expenses</div>
        </div>
      </div>

      {/* Charts Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Monthly Trends Chart */}
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Monthly Expense Trends
          </h3>
          <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', gap: '12px', paddingBottom: '40px' }}>
            {monthlyTrends.length > 0 ? (
              monthlyTrends.map((trend, index) => {
                const height = (trend.totalAmount / maxMonthlyAmount) * 100;
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return (
                  <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '100%',
                      height: `${height}%`,
                      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '4px 4px 0 0',
                      minHeight: '4px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                    title={formatCurrency(trend.totalAmount)}
                    >
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)', textAlign: 'center' }}>
                      {monthNames[trend._id.month - 1]} {trend._id.year}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ width: '100%', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)', padding: '40px' }}>
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Category Breakdown
          </h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {categoryBreakdown.length > 0 ? (
              categoryBreakdown.map((category, index) => {
                const width = (category.totalAmount / maxCategoryAmount) * 100;
                const colors = [
                  'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)',
                  'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                  'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)',
                  'linear-gradient(90deg, #fa709a 0%, #fee140 100%)',
                  'linear-gradient(90deg, #30cfd0 0%, #330867 100%)'
                ];
                return (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-brand-navy, #222448)' }}>
                        {category._id.categoryName || 'Unknown'}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                        {formatCurrency(category.totalAmount)}
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${width}%`,
                        height: '100%',
                        background: colors[index % colors.length],
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '4px' }}>
                      {category.totalItems} items
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)', padding: '40px' }}>
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status and Department Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Status Distribution */}
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Status Distribution
          </h3>
          <div>
            {statusDistribution.length > 0 ? (
              statusDistribution.map((status, index) => {
                const totalCount = statusDistribution.reduce((sum, s) => sum + s.count, 0);
                const percentage = totalCount > 0 ? (status.count / totalCount) * 100 : 0;
                return (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-brand-navy, #222448)' }}>
                        {status._id}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                        {status.count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: status._id === 'Approved' ? '#43e97b' : 
                                   status._id === 'Rejected' ? '#f5576c' : 
                                   status._id === 'Submitted' ? '#4facfe' : '#f093fb',
                        borderRadius: '4px'
                      }}></div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '4px' }}>
                      {formatCurrency(status.totalAmount)}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)', padding: '40px' }}>
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Department Breakdown */}
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Department Breakdown
          </h3>
          <div>
            {departmentBreakdown.length > 0 ? (
              departmentBreakdown.map((dept, index) => {
                return (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-brand-navy, #222448)' }}>
                        {dept._id || 'Unknown'}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                        {formatCurrency(dept.totalAmount)}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>
                      {dept.totalClaims} claims • Avg: {formatCurrency(dept.avgAmount)}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)', padding: '40px' }}>
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Expenses Table */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Top Expenses
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claim Number</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employee</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {topExpenses.length > 0 ? (
                topExpenses.map((expense, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.claimNumber}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.employeeName}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.department || '-'}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{formatDate(expense.claimDate)}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(expense.grandTotal)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: expense.status === 'Approved' ? '#d4edda' : 
                                   expense.status === 'Rejected' ? '#f8d7da' : 
                                   expense.status === 'Submitted' ? '#d1ecf1' : '#fff3cd',
                        color: expense.status === 'Approved' ? '#155724' : 
                               expense.status === 'Rejected' ? '#721c24' : 
                               expense.status === 'Submitted' ? '#0c5460' : '#856404'
                      }}>
                        {expense.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No expenses found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAnalysis;

